import { useOfflineContext } from '@providers/OfflineProvider';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AdminJobsLiveDashboardResponseDto,
  JobResponseDto,
  MeResponseDtoOrganization,
  WebsocketsService,
} from 'app/API';
import { getWebsocketConfig } from 'app/http/websocketConfig';
import { invalidateAdminJobDetails, invalidateBillingData } from 'app/queries/websocketInvalidations';
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuthContext } from './AuthProvider';

type TProps = {
  children: ReactNode;
};

export type TransactionResult = {
  id: string;
  status: 'pending' | 'success' | 'error';
};

export type TransactionProcessingEvent =
  | { type: 'organization-updated' }
  | { type: 'socket-open' }
  | { type: 'transaction-status'; data: TransactionResult };

type TransactionProcessingEventListener = (event: TransactionProcessingEvent) => void;

type TState = {
  onWSUpdate: number;
  userId?: string | null;
  updatedJob?: JobResponseDto | null;
  jobAccessLostId?: string | null;
  organizationId: string | null;
  adminJobsLiveDashboard: AdminJobsLiveDashboardResponseDto | null;
  websocketSessionId: string | null;
  newVersionDeployed: boolean;
  offline: boolean;
  notifyWSUpdate: () => void;
  subscribeTransactionProcessingEvents: (listener: TransactionProcessingEventListener) => () => void;
  transactionProcessingDisabled: boolean;
};

type ChunkBundle = {
  chunkCount: number;
  parts: (string | undefined)[];
  received: number;
  createdAt: number;
};

const WebsocketContext = createContext<TState>({
  onWSUpdate: 0,
  userId: null,
  updatedJob: null,
  jobAccessLostId: null,
  organizationId: null,
  adminJobsLiveDashboard: null,
  websocketSessionId: null,
  newVersionDeployed: false,
  offline: true,
  notifyWSUpdate: () => {},
  subscribeTransactionProcessingEvents: () => () => {},
  transactionProcessingDisabled: false,
});

type WSChunk = {
  bundleId: string;
  chunkIndex: number;
  chunkCount: number;
  chunk: string;
};

type WebsocketMessage =
  | { type: 'WSChunk'; data: WSChunk }
  | { type: 'WSUserId'; data: string }
  | { type: 'WSNewVersion'; data?: unknown }
  | { type: 'WSOrganizationId'; data: string }
  | { type: 'WSOrganizationUpdatedDto'; data: MeResponseDtoOrganization }
  | { type: 'WSTransactionStatusDto'; data: TransactionResult }
  | { type: 'WSAdminJobsLiveDashboardSnapshot'; data: AdminJobsLiveDashboardResponseDto }
  | { type: 'WSAdminJobDetailsInvalidated'; data: string }
  | { type: 'WSJobUpdatedDto'; data: JobResponseDto }
  | { type: 'WSJobAccessLost'; data: string };

const base64ToUtf8 = (b64: string): string => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    bytes[i] = bin.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
};

const safeJsonParse = <T,>(input: string): T | null => {
  try {
    return JSON.parse(input) as T;
  } catch {
    return null;
  }
};

const normalizeDisabledFlag = (value: string | undefined): boolean => {
  const v = String(value ?? '')
    .trim()
    .toLowerCase();
  return v === '1' || v === 'true' || v === 'yes' || v === 'on';
};

const buildWebsocketUrl = (host: string, stage: string, sessionId: string): string => {
  const normalizedHost = host.trim().replace(/^\/+|\/+$/g, '');
  const normalizedStage = stage.trim().replace(/^\/+|\/+$/g, '');
  const hasScheme = /^wss?:\/\//i.test(normalizedHost);
  const inferredScheme = /^(localhost|127\.0\.0\.1)(:\d+)?$/i.test(normalizedHost) ? 'ws' : 'wss';
  const origin = hasScheme ? normalizedHost : `${inferredScheme}://${normalizedHost}`;
  const token = encodeURIComponent(sessionId);
  return `${origin}/${normalizedStage}/?authToken=${token}`;
};

const WEBSOCKET_SESSION_REJECTED_CODE = 1008;

export const WebsocketProvider = ({ children }: TProps) => {
  const { updateMe, me, isLoggedIn } = useAuthContext();
  const { isOffline } = useOfflineContext();
  const queryClient = useQueryClient();

  const [newVersionDeployed, setNewVersionDeployed] = useState(false);
  const [onWSUpdate, setOnWSUpdate] = useState(0);

  const [userId, setUserId] = useState<string | null>(null);
  const [updatedJob, setJobUpdated] = useState<JobResponseDto | null | undefined>(null);
  const [jobAccessLostId, setJobAccessLostId] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [adminJobsLiveDashboard, setAdminJobsLiveDashboard] = useState<AdminJobsLiveDashboardResponseDto | null>(null);

  const [websocketSessionId, setWebsocketSessionId] = useState<string | null>(null);
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [connectTrigger, setReconnectTrigger] = useState(0);

  const bundlesRef = useRef<Map<string, ChunkBundle>>(new Map());
  const socketRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const invalidationRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const invalidationGenerationRef = useRef(0);
  const transactionProcessingEventListenersRef = useRef<Set<TransactionProcessingEventListener>>(new Set());

  const isConnectingRef = useRef(false);
  const isCreatingSessionRef = useRef(false);
  const suppressReconnectRef = useRef(false);

  const shouldBeConnectedRef = useRef(false);
  const sessionIdRef = useRef<string | null>(null);
  const handleMessageRef = useRef<(message: string) => void>(() => {});

  const [wsConfig, setWsConfig] = useState<{ host: string; stage: string } | null>(null);
  const disabled = normalizeDisabledFlag(import.meta.env.VITE_WEBSOCKET_DISABLED);

  useEffect(() => {
    if (disabled) {
      return;
    }
    let cancelled = false;
    getWebsocketConfig()
      .then((cfg) => {
        if (!cancelled) {
          setWsConfig(cfg);
        }
      })
      .catch((err) => {
        console.error('[WebsocketProvider] failed to load websocket config', err);
        Sentry.captureException(new Error('Unable to load websocket config'), { extra: { err } });
      });
    return () => {
      cancelled = true;
    };
  }, [disabled]);

  const host = wsConfig?.host ?? 'localhost:3000';
  const stage = wsConfig?.stage ?? 'test';

  const shouldBeConnected = isLoggedIn && !isOffline && !disabled && !!host && !!stage;

  useEffect(() => {
    shouldBeConnectedRef.current = shouldBeConnected;
  }, [shouldBeConnected]);

  useEffect(() => {
    sessionIdRef.current = websocketSessionId;
  }, [websocketSessionId]);

  const gcOldBundles = useCallback(() => {
    const now = Date.now();
    for (const [id, b] of bundlesRef.current.entries()) {
      if (now - b.createdAt > 5 * 60 * 1000) {
        bundlesRef.current.delete(id);
      } // 5 min TTL
    }
  }, []);

  const clearPing = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const clearInvalidationRetryTimeout = useCallback(() => {
    if (invalidationRetryTimeoutRef.current) {
      clearTimeout(invalidationRetryTimeoutRef.current);
      invalidationRetryTimeoutRef.current = null;
    }
  }, []);

  const cancelInvalidationRefresh = useCallback(() => {
    clearInvalidationRetryTimeout();
    invalidationGenerationRef.current += 1;
  }, [clearInvalidationRetryTimeout]);

  const triggerInvalidationRefresh = useCallback(() => {
    clearInvalidationRetryTimeout();
    invalidationGenerationRef.current += 1;
    const generation = invalidationGenerationRef.current;

    const runAttempt = async (attempt: number): Promise<void> => {
      const result = await updateMe({ includeNotifications: false });

      if (invalidationGenerationRef.current !== generation) {
        return;
      }

      const currentSocket = socketRef.current;
      if (!currentSocket || currentSocket.readyState !== WebSocket.OPEN) {
        return;
      }

      if (result !== undefined) {
        return;
      }

      if (attempt === 2) {
        const error = new Error('Unable to refresh user account after websocket invalidation retries');
        console.error('[WebsocketProvider] unable to refresh user account after websocket invalidation retries');
        Sentry.captureException(error);
        return;
      }

      if (invalidationGenerationRef.current !== generation || currentSocket.readyState !== WebSocket.OPEN) {
        return;
      }

      const delay = attempt === 0 ? 2000 : 5000;
      invalidationRetryTimeoutRef.current = setTimeout(() => {
        invalidationRetryTimeoutRef.current = null;

        if (invalidationGenerationRef.current !== generation) {
          return;
        }

        const retrySocket = socketRef.current;
        if (!retrySocket || retrySocket.readyState !== WebSocket.OPEN) {
          return;
        }

        void runAttempt(attempt + 1);
      }, delay);
    };

    void runAttempt(0);
  }, [clearInvalidationRetryTimeout, updateMe]);

  const scheduleReconnect = useCallback(
    (ms: number) => {
      clearReconnectTimeout();
      reconnectTimeoutRef.current = setTimeout(() => {
        setReconnectTrigger(Date.now());
      }, ms);
    },
    [clearReconnectTimeout],
  );

  const closeSocket = useCallback(() => {
    cancelInvalidationRefresh();
    clearPing();

    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }

    setSocket(null);
    isConnectingRef.current = false;
  }, [cancelInvalidationRefresh, clearPing]);

  const disconnectSession = useCallback((sessionId: string) => {
    WebsocketsService.disconnect({ xSessionId: sessionId }).catch(() => {});
  }, []);

  const flashOrganizationId = useCallback((value: string) => {
    setOrganizationId(value);
    setTimeout(() => setOrganizationId(null), 1000);
  }, []);

  const flashJobUpdated = useCallback((value: JobResponseDto) => {
    setJobUpdated(value);
    setTimeout(() => setJobUpdated(null), 1000);
  }, []);

  const flashJobAccessLost = useCallback((jobId: string) => {
    setJobAccessLostId(jobId);
    setTimeout(() => setJobAccessLostId(null), 1000);
  }, []);

  const notifyWSUpdate = useCallback(() => {
    setOnWSUpdate(Date.now());
    void invalidateBillingData(queryClient);
  }, [queryClient]);

  const subscribeTransactionProcessingEvents = useCallback((listener: TransactionProcessingEventListener) => {
    transactionProcessingEventListenersRef.current.add(listener);
    return () => {
      transactionProcessingEventListenersRef.current.delete(listener);
    };
  }, []);

  const publishTransactionProcessingEvent = useCallback((event: TransactionProcessingEvent) => {
    transactionProcessingEventListenersRef.current.forEach((listener) => listener(event));
  }, []);

  const routePayload = useCallback(
    (payload: WebsocketMessage) => {
      switch (payload.type) {
        case 'WSChunk': {
          handleChunk(payload.data);
          break;
        }
        case 'WSUserId': {
          setUserId(payload.data ?? null);
          updateMe();
          notifyWSUpdate();
          break;
        }
        case 'WSNewVersion': {
          setNewVersionDeployed(true);
          break;
        }
        case 'WSOrganizationId': {
          flashOrganizationId(payload.data);
          break;
        }
        case 'WSOrganizationUpdatedDto': {
          publishTransactionProcessingEvent({ type: 'organization-updated' });
          triggerInvalidationRefresh();
          notifyWSUpdate();
          break;
        }
        case 'WSTransactionStatusDto': {
          publishTransactionProcessingEvent({ type: 'transaction-status', data: payload.data });
          if (payload.data.status === 'success') {
            triggerInvalidationRefresh();
            notifyWSUpdate();
          }
          break;
        }
        case 'WSAdminJobsLiveDashboardSnapshot': {
          setAdminJobsLiveDashboard(payload.data);
          break;
        }
        case 'WSAdminJobDetailsInvalidated': {
          void invalidateAdminJobDetails(queryClient, payload.data);
          break;
        }
        case 'WSJobUpdatedDto': {
          void invalidateAdminJobDetails(queryClient, payload.data.id);
          flashJobUpdated(payload.data);
          break;
        }
        case 'WSJobAccessLost': {
          flashJobAccessLost(payload.data);
          break;
        }
        default: {
          console.error('Unknown type', payload);
        }
      }
    },
    [
      flashJobAccessLost,
      flashJobUpdated,
      flashOrganizationId,
      notifyWSUpdate,
      publishTransactionProcessingEvent,
      triggerInvalidationRefresh,
      updateMe,
    ],
  );

  const handleChunk = useCallback(
    (chunkDto: WSChunk) => {
      if (!chunkDto?.bundleId) {
        console.warn('[ws] Received chunk without bundleId', chunkDto);
        return;
      }

      gcOldBundles();

      let bundle = bundlesRef.current.get(chunkDto.bundleId);
      if (!bundle) {
        bundle = {
          chunkCount: chunkDto.chunkCount,
          parts: new Array(chunkDto.chunkCount),
          received: 0,
          createdAt: Date.now(),
        };
        bundlesRef.current.set(chunkDto.bundleId, bundle);
        console.log(`[ws] Started receiving bundle ${chunkDto.bundleId}, expecting ${chunkDto.chunkCount} chunks`);
      }

      if (bundle.chunkCount !== chunkDto.chunkCount) {
        console.warn(
          `[ws] Bundle ${chunkDto.bundleId} chunkCount changed from ${bundle.chunkCount} to ${chunkDto.chunkCount}, resetting`,
        );
        bundle = {
          chunkCount: chunkDto.chunkCount,
          parts: new Array(chunkDto.chunkCount),
          received: 0,
          createdAt: Date.now(),
        };
        bundlesRef.current.set(chunkDto.bundleId, bundle);
      }

      if (bundle.parts[chunkDto.chunkIndex] !== undefined) {
        console.warn(`[ws] Duplicate chunk received for bundle ${chunkDto.bundleId}, index ${chunkDto.chunkIndex}`);
        return;
      }

      bundle.parts[chunkDto.chunkIndex] = chunkDto.chunk;
      bundle.received += 1;

      console.log(
        `[ws] Received chunk ${chunkDto.chunkIndex + 1}/${bundle.chunkCount} for bundle ${chunkDto.bundleId}`,
      );

      if (bundle.received !== bundle.chunkCount) {
        return;
      }

      // Verify all parts are present before decoding
      const missingParts = bundle.parts
        .map((part, index) => (part === undefined ? index : null))
        .filter((index) => index !== null);
      if (missingParts.length > 0) {
        console.error(
          `[ws] Missing parts in bundle ${chunkDto.bundleId}:`,
          missingParts,
          `Received: ${bundle.received}/${bundle.chunkCount}`,
        );
        return;
      }

      console.log(`[ws] All chunks received for bundle ${chunkDto.bundleId}, decoding...`);
      const b64 = bundle.parts.join('');
      bundlesRef.current.delete(chunkDto.bundleId);

      try {
        const json = base64ToUtf8(b64);
        const inner = safeJsonParse<WebsocketMessage>(json);

        if (!inner?.type || inner.type === 'WSChunk') {
          console.warn('[ws] Invalid or nested chunk message after decoding', inner);
          return;
        }

        console.log('[ws] Message', inner.type, inner.data);
        routePayload(inner);
      } catch (e) {
        console.error('[ws] Failed to decode chunk bundle', e, {
          bundleId: chunkDto.bundleId,
          chunkCount: bundle.chunkCount,
          received: bundle.received,
          partsLength: bundle.parts.length,
        });
      }
    },
    [gcOldBundles, routePayload],
  );

  const handleWebsocketMessage = useCallback(
    (message: string) => {
      const payload = safeJsonParse<WebsocketMessage>(message);

      if (!payload) {
        return;
      }

      if (!payload.type) {
        return;
      }

      console.log('[ws] Message', payload.type, payload.data);

      if (payload.type === 'WSChunk') {
        handleChunk(payload.data);
        return;
      }

      routePayload(payload);
    },
    [handleChunk, routePayload],
  );

  useEffect(() => {
    handleMessageRef.current = handleWebsocketMessage;
  }, [handleWebsocketMessage]);

  const createWebsocket = useCallback(
    (sessionId: string) => {
      // Close existing socket if any
      if (socketRef.current) {
        console.log('[WebsocketProvider] Closing existing socket before creating new one');
        cancelInvalidationRefresh();
        suppressReconnectRef.current = true;
        socketRef.current.close();
        socketRef.current = null;
      }

      // Clear existing ping interval
      clearPing();

      let newSocket: WebSocket = {} as WebSocket;

      try {
        newSocket = new WebSocket(buildWebsocketUrl(host, stage, sessionId));
        socketRef.current = newSocket;
      } catch (err) {
        console.log('[WebsocketProvider] error on initialize', err);
        socketRef.current = null;
        isConnectingRef.current = false;
        return null;
      }

      newSocket.addEventListener('open', () => {
        if (socketRef.current !== newSocket) {
          return;
        }

        console.log('[WebsocketProvider] open');
        pingIntervalRef.current = setInterval(() => {
          if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send('{"type":"Ping"}');
          }
        }, 10000);
        setSocket(newSocket);
        isConnectingRef.current = false;
        triggerInvalidationRefresh();
        publishTransactionProcessingEvent({ type: 'socket-open' });
        notifyWSUpdate();
      });

      newSocket.addEventListener('message', (event) => handleMessageRef.current(event.data));

      newSocket.addEventListener('error', (error) => {
        console.log('[WebsocketProvider] error', error);
        isConnectingRef.current = false;
      });

      newSocket.addEventListener('close', (event) => {
        if (socketRef.current !== newSocket) {
          suppressReconnectRef.current = false;
          return;
        }

        console.log('[WebsocketProvider] close');
        cancelInvalidationRefresh();
        clearPing();
        setSocket(null);
        socketRef.current = null;
        isConnectingRef.current = false;

        const suppressed = suppressReconnectRef.current;
        suppressReconnectRef.current = false;

        if (event instanceof CloseEvent && event.code === WEBSOCKET_SESSION_REJECTED_CODE) {
          sessionIdRef.current = null;
          setWebsocketSessionId(null);
        }

        if (!suppressed && shouldBeConnectedRef.current && !disabled) {
          scheduleReconnect(10000);
        }
      });

      return newSocket;
    },
    [
      cancelInvalidationRefresh,
      clearPing,
      host,
      notifyWSUpdate,
      publishTransactionProcessingEvent,
      scheduleReconnect,
      stage,
      triggerInvalidationRefresh,
    ],
  );

  const ensureSession = useCallback(async () => {
    if (!shouldBeConnectedRef.current || disabled) {
      return;
    }

    if (isCreatingSessionRef.current) {
      return;
    }

    if (sessionIdRef.current) {
      return;
    }

    isCreatingSessionRef.current = true;

    try {
      const res = await WebsocketsService.create();
      isCreatingSessionRef.current = false;

      if (res.status === 401) {
        return;
      }

      if (!res.body) {
        return;
      }

      console.log('Websocket session id', res.body.sessionToken);
      setWebsocketSessionId(res.body.sessionToken);
    } catch (e: unknown) {
      isCreatingSessionRef.current = false;

      if (window.location.pathname === '/account-disabled') {
        return;
      }
      if (window.location.pathname === '/rate-limit') {
        return;
      }

      const err = e && typeof e === 'object' && 'response' in e ? (e as { response?: unknown }) : null;
      console.error('[WebsocketProvider] connect error', e);
      Sentry.captureException(new Error('Unable connect to websocket'), {
        extra: {
          response: err?.response,
          status:
            err?.response && typeof err.response === 'object' && 'status' in err.response
              ? (err.response as { status?: number }).status
              : undefined,
          statusText:
            err?.response && typeof err.response === 'object' && 'statusText' in err.response
              ? (err.response as { statusText?: string }).statusText
              : undefined,
          data:
            err?.response && typeof err.response === 'object' && 'data' in err.response
              ? (err.response as { data?: unknown }).data
              : undefined,
          user: me,
          url: window.location.href,
        },
      });

      const status =
        err?.response && typeof err.response === 'object' && 'status' in err.response
          ? (err.response as { status?: number }).status
          : undefined;
      if (status === 401) {
        console.error('Unauthorized to connect to websocket');
        Sentry.captureException(new Error('401 exception: Unauthorized to connect to websocket'));
        return;
      } else {
        scheduleReconnect(10000);
      }
    }
  }, [disabled, me, scheduleReconnect]);

  const ensureSocket = useCallback(() => {
    if (!shouldBeConnectedRef.current || disabled) {
      return;
    }

    const sessionId = sessionIdRef.current;
    if (!sessionId) {
      return;
    }

    const currentSocket = socketRef.current;

    if (currentSocket && currentSocket.readyState === WebSocket.OPEN) {
      setSocket(currentSocket);
      return;
    }

    if (currentSocket && currentSocket.readyState === WebSocket.CONNECTING) {
      return;
    }

    if (isConnectingRef.current) {
      return;
    }

    isConnectingRef.current = true;

    // Only create socket if we don't have one or if the existing one is closed
    console.log('[WebsocketProvider] Creating new websocket connection');
    const newSocket = createWebsocket(sessionId);
    if (!newSocket) {
      isConnectingRef.current = false;
      scheduleReconnect(10000);
    }
  }, [createWebsocket, disabled, scheduleReconnect]);

  useEffect(() => {
    if (!shouldBeConnected || disabled) {
      // Close socket if we're going offline or logging out
      clearReconnectTimeout();

      suppressReconnectRef.current = true;
      closeSocket();

      if (websocketSessionId) {
        disconnectSession(websocketSessionId);
      }

      setWebsocketSessionId(null);

      isConnectingRef.current = false;
      isCreatingSessionRef.current = false;

      return;
    }

    ensureSession();
    ensureSocket();
  }, [
    shouldBeConnected,
    disabled,
    connectTrigger,
    websocketSessionId,
    ensureSession,
    ensureSocket,
    closeSocket,
    disconnectSession,
    clearReconnectTimeout,
  ]);

  useEffect(() => {
    if (!isOffline && isLoggedIn) {
      setReconnectTrigger(Date.now());
    }
  }, [isLoggedIn, isOffline]);

  useEffect(() => {
    const transactionProcessingEventListeners = transactionProcessingEventListenersRef.current;
    return () => {
      transactionProcessingEventListeners.clear();
      cancelInvalidationRefresh();
      clearReconnectTimeout();
      clearPing();

      suppressReconnectRef.current = true;
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }

      if (sessionIdRef.current) {
        disconnectSession(sessionIdRef.current);
      }
    };
  }, [cancelInvalidationRefresh, clearPing, clearReconnectTimeout, disconnectSession]);

  return (
    <WebsocketContext.Provider
      value={{
        onWSUpdate,
        userId,
        updatedJob,
        jobAccessLostId,
        organizationId,
        adminJobsLiveDashboard,
        websocketSessionId,
        newVersionDeployed,
        offline: isOffline || disabled || !socket || !websocketSessionId,
        notifyWSUpdate,
        subscribeTransactionProcessingEvents,
        transactionProcessingDisabled: disabled,
      }}
    >
      {children}
    </WebsocketContext.Provider>
  );
};

export const useWebsocketContext = () => useContext(WebsocketContext);
